'use client';

import * as React from 'react';
import { DayPicker, DayProps } from 'react-day-picker';

import { cn } from '@/lib/utils';
import { buttonVariants } from '@/components/ui/button';
import {
    CalendarApartmentResponseDto,
    CalendarEventType,
    DocumentType,
} from '@/features/calendar/types/calendar-dto';
import { addMonths, isWithinInterval, startOfDay, subDays, subMonths } from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const CalendarEventConfig = (type: CalendarEventType) => {
    switch (type) {
        case CalendarEventType.Reservation:
            return {
                eventColor: 'bg-warning-1000',
            };
        case CalendarEventType.Accommodation:
            return {
                eventColor: 'bg-info-1000',
            };
        case CalendarEventType.HouseUse:
            return {
                eventColor: 'bg-success-1000',
            };
    }
};

type CalendarProps = {
    apartments?: CalendarApartmentResponseDto[];
    selectedDate: Date;
    setSelectedDate: (date: Date) => void;
    showReservationDetails?: (
        id: string,
        type: DocumentType,
        apartmentId?: string,
        checkInDate?: Date,
        checkOutDate?: Date,
    ) => void;
} & React.ComponentProps<typeof DayPicker>;

function BookingCalendarSingle({
    className,
    classNames,
    showOutsideDays = false,
    apartments = [],
    selectedDate,
    setSelectedDate,
    showReservationDetails,
    ...props
}: CalendarProps) {
    const [displayedMonth, setDisplayedMonth] = React.useState<Date>(selectedDate);

    const renderDay = ({ date }: DayProps) => {
        if (!date) return null;

        const apartmentEvents = apartments.flatMap(
            (apartment) =>
                apartment.documents
                    ?.filter((doc) =>
                        isWithinInterval(date, {
                            start: startOfDay(doc.checkInDate),
                            end: startOfDay(subDays(doc.checkOutDate, 1)),
                        }),
                    )
                    .map((doc) => ({
                        ...doc,
                        apartmentId: apartment.apartmentId,
                    })) || [],
        );

        return (
            <div className="relative size-9 flex items-center justify-center ">
                <span>{date.getDate()}</span>
                {apartmentEvents.map((doc, index) => (
                    <>
                        <div
                            key={index}
                            className={cn(
                                'absolute w-full h-0.5 bottom-0',
                                CalendarEventConfig(doc.eventType)?.eventColor,
                            )}
                        />
                        <div
                            className="absolute inset-0 cursor-pointer"
                            onClick={() =>
                                showReservationDetails?.(
                                    doc.documentId,
                                    doc.documentType,
                                    doc.apartmentId,
                                    doc.checkInDate,
                                    doc.checkOutDate,
                                )
                            }
                        />
                    </>
                ))}
            </div>
        );
    };

    const previousPeriodHandler = () => {
        const newDate = subMonths(displayedMonth, 1);
        setDisplayedMonth(newDate);
        if (newDate.getFullYear() !== selectedDate.getFullYear()) {
            setSelectedDate(newDate);
        }
    };

    const nextPeriodHandler = () => {
        const newDate = addMonths(displayedMonth, 1);
        setDisplayedMonth(newDate);
        if (newDate.getFullYear() !== selectedDate.getFullYear()) {
            setSelectedDate(newDate);
        }
    };

    return (
        <DayPicker
            fixedWeeks
            weekStartsOn={1}
            showOutsideDays={showOutsideDays}
            month={displayedMonth}
            onMonthChange={setDisplayedMonth}
            className={cn('w-full', className)}
            classNames={{
                month: 'space-y-4 select-none',
                table: 'w-full',
                caption: 'flex justify-center',
                caption_label: 'text-sm font-medium text-popover-1000',
                head_row: 'flex',
                head_cell: 'w-full text-xs font-normal',
                row: 'flex w-full',
                cell: 'w-full p-0 text-center relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20',
                day: cn(
                    buttonVariants({ variant: 'ghost' }),
                    'size-9 p-0 text-xs font-normal aria-selected:opacity-100',
                ),
                day_today: 'bg-popover-100',
                day_outside: 'opacity-50',
                day_disabled: 'opacity-50',
                day_hidden: 'invisible',
                ...classNames,
            }}
            components={{
                Caption: ({ displayMonth }) => (
                    <div className="flex justify-between items-center w-full px-2">
                        <button
                            onClick={previousPeriodHandler}
                            className="text-gray-600 hover:text-gray-800"
                        >
                            <ChevronLeft className="size-5" />
                        </button>
                        <span className="text-sm font-medium">
                            {displayMonth.toLocaleDateString('en-US', {
                                month: 'long',
                            })}
                        </span>
                        <button
                            onClick={nextPeriodHandler}
                            className="text-gray-600 hover:text-gray-800"
                        >
                            <ChevronRight className="size-5" />
                        </button>
                    </div>
                ),
                DayContent: renderDay,
            }}
            {...props}
        />
    );
}

BookingCalendarSingle.displayName = 'Calendar';

export { BookingCalendarSingle };
